import { observable } from "@legendapp/state";
import { getVideoFunnelReport } from "@/apis/clients/reportingClient";
import {
  createRequestGuard,
  reportScopeKey,
  toErrorMessage,
} from "@/stores/storeHelpers";
import type { PerformanceReportRow, ReportScope } from "@/types/reporting";

interface VideoFunnelState {
  rows: PerformanceReportRow[];
  isLoading: boolean;
  error: string | null;
  loadedForScopeKey: string | null;
}

const DEFAULT_VALUES: VideoFunnelState = {
  rows: [],
  isLoading: false,
  error: null,
  loadedForScopeKey: null,
};

export const VideoFunnelStore$ = observable(DEFAULT_VALUES);

const getScopeKey = (scope: ReportScope) =>
  `video-funnel:${reportScopeKey(scope)}`;

const guard = createRequestGuard();

export const fetchVideoFunnelReport = async (scope: ReportScope) => {
  const scopeKey = getScopeKey(scope);
  const result = guard.check(
    scopeKey,
    VideoFunnelStore$.loadedForScopeKey.get()
  );
  if (result.outcome === "cached") {
    VideoFunnelStore$.assign({ error: null, isLoading: false });
    return;
  }
  if (result.outcome === "in-flight") return;
  const { requestId } = result;
  VideoFunnelStore$.assign({ isLoading: true, error: null });

  try {
    const response = await getVideoFunnelReport(scope);
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    VideoFunnelStore$.assign({
      rows: response.results,
      loadedForScopeKey: scopeKey,
    });
  } catch (error) {
    if (guard.isStale(requestId)) return;
    guard.clearPending(scopeKey);
    VideoFunnelStore$.error.set(toErrorMessage(error));
  } finally {
    if (!guard.isStale(requestId)) {
      VideoFunnelStore$.isLoading.set(false);
    }
  }
};

export const clearVideoFunnelReport = () => {
  guard.invalidate();
  VideoFunnelStore$.assign({
    rows: [],
    isLoading: false,
    loadedForScopeKey: null,
    error: null,
  });
};
